import React, { useEffect, useState } from 'react';
import { X, DollarSign, Calendar, CreditCard, FileText } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { addPayment, updatePayment } from '../../services/paymentService';
import type { Payment } from '../../services/paymentService';
import { getTeacherStudents } from '../../services/studentService';
import { Timestamp } from 'firebase/firestore';
interface AddPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  teacherId: string;
  editPayment?: Payment | null;
  onSuccess: () => void;
}
export const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
  isOpen,
  onClose,
  teacherId,
  editPayment,
  onSuccess
}) => {
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  // Helper function to get local date string in YYYY-MM-DD format
  const getLocalDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  // Form state
  const [studentId, setStudentId] = useState('');
  const [studentName, setStudentName] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(getLocalDateString);
  const [method, setMethod] = useState<Payment['method']>('Cash');
  const [notes, setNotes] = useState('');
  // UI state
  const [students, setStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingStudents, setLoadingStudents] = useState(true);
  // Load students on mount
  useEffect(() => {
    const loadStudents = async () => {
      try {
        setLoadingStudents(true);
        const fetchedStudents = await getTeacherStudents(teacherId);
        setStudents(fetchedStudents);
      } catch (err) {
        console.error('Error loading students:', err);
        setError('Failed to load students');
      } finally {
        setLoadingStudents(false);
      }
    };
    if (isOpen && teacherId) {
      loadStudents();
    }
  }, [isOpen, teacherId]);
  // Populate form when editing
  useEffect(() => {
    if (editPayment) {
      setStudentId(editPayment.studentId);
      setStudentName(editPayment.studentName);
      setAmount(editPayment.amount.toString());
      setMethod(editPayment.method);
      setNotes(editPayment.notes || '');
      // Convert Timestamp to date string
      const paidAtDate = editPayment.paidAt instanceof Timestamp ? editPayment.paidAt.toDate() : new Date(editPayment.paidAt);
      setDate(paidAtDate.toISOString().split('T')[0]);
    } else {
      // Reset form for new payment
      setStudentId('');
      setStudentName('');
      setAmount('');
      setDate(getLocalDateString());
      setMethod('Cash');
      setNotes('');
    }
    setError('');
  }, [editPayment, isOpen]);
  // Handle student selection
  const handleStudentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    setStudentId(selectedId);
    const selectedStudent = students.find(s => s.id === selectedId);
    if (selectedStudent) {
      setStudentName(selectedStudent.name);
    }
  };
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    // Validation
    if (!studentId) {
      setError('Please select a student');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (!date) {
      setError('Please select a date');
      return;
    }
    try {
      setLoading(true);
      // Build payment data object
      const paymentData: any = {
        studentId,
        studentName,
        amount: parseFloat(amount),
        currency: 'USD',
        method,
        paidAt: new Date(date)
      };
      // Only add notes if not empty (Firestore doesn't accept undefined)
      const trimmedNotes = notes.trim();
      if (trimmedNotes) {
        paymentData.notes = trimmedNotes;
      }
      if (editPayment?.id) {
        // Update existing payment
        await updatePayment(teacherId, editPayment.id, paymentData);
      } else {
        // Add new payment
        await addPayment(teacherId, paymentData);
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error saving payment:', err);
      setError(err.message || 'Failed to save payment');
    } finally {
      setLoading(false);
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-id="element-1894">
      <div className={`w-full max-w-md rounded-xl shadow-xl ${isDark ? 'bg-navy-800 text-white' : 'bg-white text-gray-800'} max-h-[90vh] overflow-y-auto`} data-id="element-1895">
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-1896">
          <h2 className="text-xl font-bold" data-id="element-1897">
            {editPayment ? 'Edit Payment' : 'Add Payment'}
          </h2>
          <button onClick={onClose} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700' : 'hover:bg-gray-100'}`} disabled={loading} data-id="element-1898">
            <X className="h-5 w-5" data-id="element-1899" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-id="element-1900">
          {/* Error message */}
          {error && <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm" data-id="element-1901">
              {error}
            </div>}

          {/* Student selection */}
          <div data-id="element-1902">
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-1903">
              Student <span className="text-red-500" data-id="element-1904">*</span>
            </label>
            <select value={studentId} onChange={handleStudentChange} disabled={loadingStudents || loading} required className={`w-full px-4 py-2 rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-800 focus:ring-deep-pink focus:border-deep-pink'} transition-colors disabled:opacity-50`} data-id="element-1905">
              <option value="" data-id="element-1906">
                {loadingStudents ? 'Loading students...' : 'Select a student'}
              </option>
              {students.map(student => <option key={student.id} value={student.id} data-id="element-1907">
                  {student.name}
                </option>)}
            </select>
          </div>

          {/* Amount */}
          <div data-id="element-1908">
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-1909">
              Amount <span className="text-red-500" data-id="element-1910">*</span>
            </label>
            <div className="relative" data-id="element-1911">
              <DollarSign className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1912" />
              <input type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" required disabled={loading} className={`w-full pl-10 pr-4 py-2 rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-800 focus:ring-deep-pink focus:border-deep-pink'} transition-colors disabled:opacity-50`} data-id="element-1913" />
            </div>
          </div>

          {/* Date */}
          <div data-id="element-1914">
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-1915">
              Date <span className="text-red-500" data-id="element-1916">*</span>
            </label>
            <div className="relative" data-id="element-1917">
              <Calendar className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'} pointer-events-none`} data-id="element-1918" />
              <input type="date" value={date} onChange={e => setDate(e.target.value)} required disabled={loading} className={`w-full pl-10 pr-4 py-2 rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-800 focus:ring-deep-pink focus:border-deep-pink'} transition-colors disabled:opacity-50 h-[42px]`} style={{
              WebkitAppearance: 'none',
              MozAppearance: 'textfield'
            }} data-id="element-1919" />
            </div>
          </div>

          {/* Payment method */}
          <div data-id="element-1920">
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-1921">
              Payment Method <span className="text-red-500" data-id="element-1922">*</span>
            </label>
            <div className="relative" data-id="element-1923">
              <CreditCard className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1924" />
              <select value={method} onChange={e => setMethod(e.target.value as Payment['method'])} required disabled={loading} className={`w-full pl-10 pr-4 py-2 rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-800 focus:ring-deep-pink focus:border-deep-pink'} transition-colors disabled:opacity-50`} data-id="element-1925">
                <option value="Cash" data-id="element-1926">Cash</option>
                <option value="Zelle" data-id="element-1927">Zelle</option>
                <option value="Venmo" data-id="element-1928">Venmo</option>
                <option value="Check" data-id="element-1929">Check</option>
                <option value="Other" data-id="element-1930">Other</option>
              </select>
            </div>
          </div>

          {/* Notes */}
          <div data-id="element-1931">
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-1932">
              Notes (Optional)
            </label>
            <div className="relative" data-id="element-1933">
              <FileText className={`absolute left-3 top-3 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1934" />
              <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add any notes about this payment..." rows={3} disabled={loading} className={`w-full pl-10 pr-4 py-2 rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white border-gray-300 text-gray-800 focus:ring-deep-pink focus:border-deep-pink'} transition-colors disabled:opacity-50 resize-none`} data-id="element-1935" />
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 pt-4" data-id="element-1936">
            <button type="button" onClick={onClose} disabled={loading} className={`flex-1 px-4 py-2 rounded-lg border ${isDark ? 'border-navy-700 hover:bg-navy-700' : 'border-gray-300 hover:bg-gray-50'} transition-colors disabled:opacity-50`} data-id="element-1937">
              Cancel
            </button>
            <button type="submit" disabled={loading || loadingStudents} className={`flex-1 px-4 py-2 rounded-lg border-2 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDark ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10'}`} data-id="element-1938">
              {loading ? 'Saving...' : editPayment ? 'Update Payment' : 'Add Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>;
};